import React, { useMemo } from 'react';
import cn from 'classnames';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '$/components/DropdownMenu';
import IconFont from '$/components/IconFont';
import ScrollArea from '$/components/ScrollArea';
import { useLanguageStore } from '$/store/languageStore';
import PanelItem from '$/pages/RoboneoTeamFlow/FlowContent/components/NodeBottomPanel/PanelItem';
import Ratio, { RatioIcon } from '$/pages/RoboneoTeamFlow/FlowContent/components/NodeBottomPanel/Ratio';
import SelectRows from '$/pages/RoboneoTeamFlow/FlowContent/components/NodeBottomPanel/SelectRows';
import type { RoleGenModelParameterValue } from './RoleSettingForm';
import type { RoleGenModelOption } from './useModelOptions';
import styles from './index.module.scss';

interface RoleModelControlsProps {
  models: RoleGenModelOption[];
  loading: boolean;
  selectedModel?: RoleGenModelOption;
  selectedParameters: Record<string, RoleGenModelParameterValue>;
  onModelChange: (modelKey: string) => void;
  onParameterChange: (key: string, value: RoleGenModelParameterValue) => void;
}

type RoleModelParameterGroup = {
  key: string;
  label: string;
  type?: string;
  value: RoleGenModelParameterValue;
  selectedLabel: string;
  options: Array<{
    value: RoleGenModelParameterValue;
    label: string;
    icon?: string;
  }>;
};

/** 模型图标优先使用服务端 iconfont，缺失时回退通用 AI 模型图标。 */
function renderRoleModelIcon(icon?: string): React.ReactElement {
  return <IconFont name={icon || 'aiModelBold'} fontSize={16} color="currentColor" />;
}

/** 将后端参数选项的展示文案统一转为字符串，避免 ReactNode 进入底部紧凑触发器。 */
function normalizeOptionLabel(value: RoleGenModelParameterValue, label: unknown): string {
  if (typeof label === 'string' || typeof label === 'number') return String(label);
  return String(value);
}

/** 从当前角色模型参数中提取底部配置菜单可展示的枚举项。 */
function getRoleModelParameterGroups(
  selectedModel: RoleGenModelOption | undefined,
  selectedParameters: Record<string, RoleGenModelParameterValue>,
): RoleModelParameterGroup[] {
  if (!selectedModel) return [];

  return Object.entries(selectedModel.parameterConfigs).reduce<RoleModelParameterGroup[]>((groups, [key, config]) => {
    if (!config?.options?.length) return groups;
    if (config.type !== 'ratio' && config.type !== 'select') return groups;

    const value = selectedParameters[key]
      ?? selectedModel.parameters[key]
      ?? config.default
      ?? config.options[0]?.value
      ?? '';
    if (typeof value === 'object') return groups;

    const options = config.options.map((option) => ({
      value: option.value,
      label: normalizeOptionLabel(option.value, option.label),
      icon: option.icon,
    }));
    const selectedLabel = options.find((option) => String(option.value) === String(value))?.label ?? String(value);

    groups.push({
      key,
      label: config.name || key,
      type: config.type,
      value,
      selectedLabel,
      options,
    });
    return groups;
  }, []);
}

/** 按画布配置面板的 ratio/select 控件渲染角色创建参数项。 */
function renderRoleModelParameterControl(
  group: RoleModelParameterGroup,
  onParameterChange: (key: string, value: RoleGenModelParameterValue) => void,
): React.ReactNode {
  if (group.type === 'ratio') {
    return (
      <Ratio
        value={group.value}
        options={group.options}
        onChange={(nextValue) => onParameterChange(group.key, nextValue as RoleGenModelParameterValue)}
      />
    );
  }

  return (
    <SelectRows
      value={group.value}
      options={group.options}
      onValueChange={(nextValue) => onParameterChange(group.key, nextValue as RoleGenModelParameterValue)}
    />
  );
}

/** 角色创建底部模型选择和参数配置入口，复用画布 DropdownMenu 交互风格。 */
function RoleModelControls({
  models,
  loading,
  selectedModel,
  selectedParameters,
  onModelChange,
  onParameterChange,
}: RoleModelControlsProps): React.ReactElement {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const parameterGroups = useMemo(
    () => getRoleModelParameterGroups(selectedModel, selectedParameters),
    [selectedModel, selectedParameters],
  );

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(styles.modelSelect, { [styles.modelSelectDisabled]: loading || models.length === 0 })}
          showChevron={!loading && models.length > 0}
        >
          {renderRoleModelIcon(selectedModel?.icon)}
          <span>
            {loading
              ? languageStore.smartcatTranslationText((value) => value.RoboNeo_Web_loading1)
              : selectedModel?.name
                ?? languageStore.smartcatTranslationText((value) => value.no_models_available)}
          </span>
        </DropdownMenuTrigger>
        {!loading && models.length > 0 && (
          <DropdownMenuContent
            align="start"
            side="top"
            sideOffset={8}
            className={styles.roleModelMenu}
          >
            <ScrollArea
              className={styles.roleModelScrollArea}
              viewportClassName={styles.roleModelViewport}
              orientation="vertical"
              scrollbarSize="thin"
              hideDelay={0}
            >
              {models.map((model) => {
                const selected = selectedModel?.key === model.key;
                return (
                  <DropdownMenuItem
                    key={model.key}
                    className={cn(styles.roleModelItem, selected && styles.roleModelItemSelected)}
                    selected={selected}
                    onSelect={() => onModelChange(model.key)}
                  >
                    <span className={styles.roleModelIcon}>{renderRoleModelIcon(model.icon)}</span>
                    <span className={styles.roleModelText}>
                      <span className={styles.roleModelName}>{model.name}</span>
                      {model.description && (
                        <span className={styles.roleModelDesc} title={model.description}>
                          {model.description}
                        </span>
                      )}
                    </span>
                    {selected && <IconFont name="checkV2Fill" fontSize={12} color="currentColor" />}
                  </DropdownMenuItem>
                );
              })}
            </ScrollArea>
          </DropdownMenuContent>
        )}
      </DropdownMenu>

      {parameterGroups.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger className={styles.roleParamTrigger} showChevron>
            <span className={styles.roleParamTriggerGroup}>
              {parameterGroups.slice(0, 2).map((group, index) => (
                <React.Fragment key={group.key}>
                  {index > 0 && <span className={styles.roleParamTriggerDivider} />}
                  <span className={styles.roleParamTriggerValue}>
                    {group.type === 'ratio' && <RatioIcon value={String(group.value)} size={16} />}
                    <span>{group.selectedLabel}</span>
                  </span>
                </React.Fragment>
              ))}
            </span>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="top"
            sideOffset={8}
            className={styles.roleParamMenu}
          >
            <div className={styles.roleParamPanel}>
              {parameterGroups.map((group) => (
                <PanelItem
                  key={group.key}
                  name={group.label}
                  className={styles.roleParamPanelItem}
                >
                  {renderRoleModelParameterControl(group, onParameterChange)}
                </PanelItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

export default RoleModelControls;
